// Modules
import React, { useEffect, useState, ReactNode } from "react";
import axios from "axios";

// Components
import HeaderContent from "containers/HeaderContent/HeaderContent";
import Footer from "components/shared/Footer/Footer";
import SchemaScript from "components/shared/Schema/SchemaScript";
import CookieBanner from "components/shared/CookieBanner/CookieBanner";
import GoogleTagManager from "components/shared/GoogleTagManager/GoogleTagManager";
import GlobalWkPageData from "./shared/GoogleTagManager/GlobalWkPageData";
import GoogleTagManagerNoScript from "components/shared/GoogleTagManager/GoogleTagManagerNoScript";
import PageLoading from "components/shared/PageLoading/PageLoading";
import MetaTags from "components/shared/MetaTags/MetaTags";

// Constants
import { GENERIC_GTM_ID, TDM_META_TAGS } from "constants/shared/pages";
import { SCHEMA_DEFAULT_LDJSON } from "constants/shared/schema";
import { defaultUserInfo } from "constants/shared/userInfo";
import { BeApiServiceEnum } from "constants/searchPage/BeApiEnum";

// Redux
import { useAppSelector } from "redux/hooks";
import { htmlFooterSelector } from "redux/shared/userInfoSlice";

// Interfaces
import { UserInfo } from "interfaces/shared/userInfo";

// Services
import { APIGetUserInfoNoCache } from "services/beApi/userInfoService";

const ENABLE_LOADING = process.env.NEXT_PUBLIC_ENABLE_LOADING === "true";
const ENABLE_PASSIVE_MODE = process.env.NEXT_PUBLIC_ENABLE_PASSIVE_MODE === "true";

export interface LayoutProps {
    children?: ReactNode;
}

const MainLayout = ({ children }: LayoutProps) => {
    const [loading, setLoading] = useState(ENABLE_LOADING);
    const [userInfoLoading, setUserInfoLoading] = useState(true);
    const [handleSessionCompleted, setHandleSessionCompleted] = useState(false);
    const [userInfoClientSide, setUserInfoClientSide] = useState<UserInfo>(defaultUserInfo);

    const htmlFooter = useAppSelector(htmlFooterSelector);
    const effectiveHtmlFooter = htmlFooter || userInfoClientSide.htmlFooter;

    // Handle session
    useEffect(() => {
        const fetchSession = async () => {
            try {
                const fullPath = window.location.pathname + window.location.search;
                const encodedRedirectUrl = encodeURIComponent(fullPath);
                const response = await axios.get(`/${process.env.NEXT_PUBLIC_SEARCH_PATH_NAME}/api/handleSession?redirectUrl=${encodedRedirectUrl}`);
                const loginUrl = response.data.message;

                if (loginUrl) {
                    window.location.replace(loginUrl);
                    return; // stop execution if redirecting
                }
            } catch (error) {
                console.error("SSO error:", error);
            } finally {
                setHandleSessionCompleted(true);
                setLoading(false)
            }
        };

        fetchSession();
    }, []);

    // Fetch user info
    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const response = await APIGetUserInfoNoCache(BeApiServiceEnum.USER_INFO_SERVICE);
                setUserInfoClientSide(response.data);
            } catch (error) {
                console.error("UserInfo error:", error);
            } finally {
                setUserInfoLoading(false);
            }
        };

        if (ENABLE_PASSIVE_MODE) {
            if (handleSessionCompleted) {
                fetchUserInfo();
            }
        } else {
            fetchUserInfo();
        }
    }, [handleSessionCompleted]);

    return (
      <>
          {loading && <PageLoading />}
          <div style={{ display: loading ? "none" : "block" }}>
              <MetaTags metaTags={TDM_META_TAGS} />
              <CookieBanner />
              <GlobalWkPageData />
              <GoogleTagManager isInHead={true} gtmId={GENERIC_GTM_ID} />
              <SchemaScript ldJson={SCHEMA_DEFAULT_LDJSON} />
              <GoogleTagManagerNoScript gtmId={GENERIC_GTM_ID} />
              <div className="wk-centered-container">
                  <HeaderContent loading={userInfoLoading} userInfoClientSide={userInfoClientSide} />
                  <main>{children}</main>
                  <Footer htmlFooter={effectiveHtmlFooter} />
              </div>
          </div>
      </>
    );
};

export default MainLayout;
